<template>
  <section class="methodology-page" :dir="pageDirection">
    <div class="section-head">
      <h2>{{ tr("title") }}</h2>
      <p class="desc">{{ tr("description") }}</p>
    </div>

    <div class="card">
      <h3>
        <v-icon size="18">mdi-book-open-page-variant-outline</v-icon>
        {{ tr("framework.title") }}
      </h3>

      <p>{{ tr("framework.paragraph1") }}</p>
      <p>{{ tr("framework.paragraph2") }}</p>
    </div>

    <div class="card">
      <h3>
        <v-icon size="18">mdi-file-document-multiple-outline</v-icon>
        {{ tr("references.title") }}
      </h3>

      <div class="table-wrap desktop-table">
        <table>
          <thead>
            <tr>
              <th>{{ tr("references.table.document") }}</th>
              <th>{{ tr("references.table.version") }}</th>
              <th>{{ tr("references.table.usage") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in references" :key="item.key">
              <td>{{ item.document }}</td>
              <td>{{ item.version }}</td>
              <td>{{ item.usage }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="reference-cards">
        <article
          v-for="item in references"
          :key="`mobile-${item.key}`"
          class="reference-card"
        >
          <h4>{{ item.document }}</h4>

          <div class="reference-row">
            <span>{{ tr("references.table.version") }}</span>
            <strong>{{ item.version }}</strong>
          </div>

          <div class="reference-row">
            <span>{{ tr("references.table.usage") }}</span>
            <strong>{{ item.usage }}</strong>
          </div>
        </article>
      </div>
    </div>

    <div class="card limits-card">
      <h3>
        <v-icon size="18">mdi-alert-circle-outline</v-icon>
        {{ tr("limitations.title") }}
      </h3>

      <p>{{ tr("limitations.paragraph1") }}</p>
      <p>{{ tr("limitations.paragraph2") }}</p>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const tr = (key, params = {}) => t(`methodologyPage.${key}`, params);

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const references = computed(() => [
  {
    key: "assessmentReport",
    document: tr("references.items.assessmentReport.document"),
    version: tr("references.items.assessmentReport.version"),
    usage: tr("references.items.assessmentReport.usage"),
  },
  {
    key: "analysisGuide",
    document: tr("references.items.analysisGuide.document"),
    version: tr("references.items.analysisGuide.version"),
    usage: tr("references.items.analysisGuide.usage"),
  },
  {
    key: "hfasForm",
    document: tr("references.items.hfasForm.document"),
    version: tr("references.items.hfasForm.version"),
    usage: tr("references.items.hfasForm.usage"),
  },
  {
    key: "inceptionReport",
    document: tr("references.items.inceptionReport.document"),
    version: tr("references.items.inceptionReport.version"),
    usage: tr("references.items.inceptionReport.usage"),
  },
]);
</script>

<style scoped>
.methodology-page {
  --navy: #15355c;
  --bg: #f4f6f9;
  --surface: #ffffff;
  --surface-2: #f8fafc;
  --border: #dde4ec;
  --text: #1a2433;
  --text-2: #4c5b6e;
  --text-3: #7a8799;
  --warning: #b45309;
  --warning-bg: #fff7ed;
  --shadow-sm: 0 1px 2px rgba(13, 34, 64, 0.06);
  --radius: 12px;

  min-height: 100vh;
  padding: 22px 26px 60px;
  overflow-x: hidden;
  background: var(--bg);
  color: var(--text);
  font-family: "Inter", "Cairo", sans-serif;
}

.methodology-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}

.methodology-page[dir="rtl"] .section-head,
.methodology-page[dir="rtl"] .card {
  text-align: right;
}

.methodology-page[dir="ltr"] .section-head,
.methodology-page[dir="ltr"] .card {
  text-align: left;
}

.section-head {
  margin-bottom: 18px;
}

.section-head h2 {
  margin: 0;
  color: var(--text);
  font-size: 23px;
  font-weight: 800;
  line-height: 1.5;
}

.section-head .desc {
  max-width: 850px;
  margin: 5px 0 0;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.8;
}

.card {
  padding: 18px;
  margin-bottom: 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-inline-start: 4px solid var(--navy);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}

.card h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  color: var(--text);
  font-size: 15.5px;
  font-weight: 800;
  line-height: 1.5;
}

.card h3 :deep(.v-icon) {
  flex-shrink: 0;
  color: var(--navy);
}

.card p {
  margin: 0 0 8px;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.9;
}

.card p:last-child {
  margin-bottom: 0;
}

.limits-card {
  border-inline-start-color: var(--warning);
  background: linear-gradient(to bottom, var(--surface), var(--warning-bg));
}

.limits-card h3 :deep(.v-icon) {
  color: var(--warning);
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  -webkit-overflow-scrolling: touch;
}

table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  background: var(--surface);
  font-size: 13px;
}

thead th {
  padding: 11px 12px;
  color: #fff;
  font-weight: 700;
  text-align: start;
  white-space: nowrap;
  background: var(--navy);
}

tbody td {
  padding: 11px 12px;
  color: var(--text-2);
  vertical-align: top;
  line-height: 1.7;
  border-top: 1px solid var(--border);
}

tbody td:first-child {
  color: var(--text);
  font-weight: 700;
}

tbody tr:hover {
  background: var(--surface-2);
}

.reference-cards {
  display: none;
}

@media (max-width: 768px) {
  .methodology-page {
    min-height: 100vh;
    padding: 16px 10px 40px;
  }

  .section-head {
    margin-bottom: 15px;
  }

  .section-head h2 {
    font-size: 21px;
  }

  .section-head .desc {
    font-size: 12px;
    line-height: 1.75;
  }

  .card {
    padding: 14px;
    margin-bottom: 12px;
    border-radius: 14px;
  }

  .card h3 {
    align-items: flex-start;
    font-size: 15px;
    line-height: 1.6;
  }

  .card p {
    font-size: 12px;
    line-height: 1.85;
  }

  .desktop-table {
    display: none;
  }

  .reference-cards {
    display: grid;
    gap: 12px;
  }

  .reference-card {
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--border);
    border-inline-start: 4px solid var(--navy);
    border-radius: 12px;
  }

  .reference-card h4 {
    margin: 0;
    padding: 12px;
    color: var(--text);
    font-size: 13px;
    font-weight: 800;
    line-height: 1.7;
    background: var(--surface-2);
    border-bottom: 1px solid var(--border);
    overflow-wrap: break-word;
  }

  .reference-row {
    display: grid;
    grid-template-columns: 105px minmax(0, 1fr);
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px dashed var(--border);
  }

  .reference-row:last-child {
    border-bottom: 0;
  }

  .reference-row span {
    color: var(--text-3);
    font-size: 11px;
    font-weight: 700;
  }

  .reference-row strong {
    min-width: 0;
    color: var(--text-2);
    font-size: 11.5px;
    font-weight: 700;
    line-height: 1.7;
    overflow-wrap: break-word;
  }
}

@media (max-width: 420px) {
  .methodology-page {
    padding-inline: 8px;
  }

  .reference-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
